import type { ProjectDetail } from "@esi/shared";
import { Ellipsis, GitCompareArrows, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "../i18n/format.ts";
import { canChange } from "../projects/capabilities.ts";
import { shortId } from "../projects/readers.ts";
import { changeSetPath, Link, navigate, pageComparePath, wikiPath } from "../router.tsx";
import { useChangeSet, useWikiActivity, useWikiPage, useWikiVersions } from "./queries.ts";
import { RestoreDialog, type RestoreTarget } from "./RestoreDialog.tsx";

export function PageHistory({ project, pageId }: { project: ProjectDetail; pageId: string }) {
  const { t } = useTranslation();
  const page = useWikiPage(project.id, pageId, undefined);
  const versions = useWikiVersions(project.id, pageId);
  // Selection order: a third choice replaces the earliest one.
  const [selected, setSelected] = useState<number[]>([]);
  const [restore, setRestore] = useState<RestoreTarget | null>(null);
  // Each row's Restore button (desktop) and actions menu (phone): focus returns to the visible one.
  const triggers = useRef(new Map<string, HTMLButtonElement | null>());
  const restoredFrom = useRef<number | null>(null);
  if (page.isError || versions.isError) {
    return <Notice tone="danger">{(page.error ?? versions.error)?.message}</Notice>;
  }
  if (!page.data || !versions.data) {
    return <Skeleton className="h-48" />;
  }
  const current = page.data;
  const canRestore = canChange(project, "wiki.draft");
  const toggle = (version: number, on: boolean) =>
    setSelected((was) =>
      on
        ? [...was.filter((v) => v !== version), version].slice(-2)
        : was.filter((v) => v !== version),
    );
  const [a, b] = selected;
  const openRestore = (version: number) => {
    restoredFrom.current = version;
    setRestore({ kind: "version", pageId, version, title: current.title });
  };
  return (
    <section data-esi-content="wiki" className="mx-auto flex max-w-3xl flex-col gap-5">
      <header className="flex flex-col gap-2">
        <Link
          href={wikiPath(project.id, pageId)}
          className="text-sm text-accent-text hover:underline"
        >
          {current.title}
        </Link>
        <h2 className="text-xl font-semibold tracking-tight text-fg">{t("wiki.docs.history")}</h2>
      </header>
      {versions.data.length > 1 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p id="history-compare-hint" className="text-xs text-fg-secondary">
            {t("wiki.history.compareHint")}
          </p>
          <Button
            variant="outline"
            size="sm"
            aria-describedby="history-compare-hint"
            disabled={a === undefined || b === undefined}
            onClick={() => {
              if (a !== undefined && b !== undefined) {
                navigate(pageComparePath(project.id, pageId, a, b));
              }
            }}
          >
            <GitCompareArrows aria-hidden="true" />
            {t("wiki.history.compare")}
          </Button>
        </div>
      ) : null}
      <ol className="divide-y divide-line rounded-lg border border-line">
        {versions.data.map((version, index) => {
          const previous = versions.data[index + 1];
          const restorable = canRestore && version.version !== current.version;
          const changes = previous
            ? pageComparePath(project.id, pageId, previous.version, version.version)
            : null;
          return (
            <li key={version.version} className="flex items-start gap-3 px-4 py-4">
              {versions.data.length > 1 ? (
                <Checkbox
                  className="mt-0.5"
                  aria-label={t("wiki.history.select", { version: version.version })}
                  checked={selected.includes(version.version)}
                  onCheckedChange={(on) => toggle(version.version, on === true)}
                />
              ) : null}
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <p className="flex flex-wrap items-baseline gap-x-2">
                  <Link
                    href={wikiPath(project.id, pageId, version.version)}
                    className="text-sm font-medium text-fg hover:underline"
                  >
                    {t("wiki.version", { version: version.version })}
                  </Link>
                  {version.version === current.version ? (
                    <span className="text-xs text-fg-muted">{t("wiki.history.current")}</span>
                  ) : null}
                </p>
                <p className="text-xs text-fg-secondary">
                  {version.authorName ?? t("wiki.draft.provenance.someone")} ·{" "}
                  {formatDateTime(version.publishedAt)}
                  {version.origin ? <> · {t(`wiki.changes.origin.${version.origin}`)}</> : null}
                </p>
                {version.publishedBy ? (
                  <p className="text-xs text-fg-secondary">
                    {t("wiki.changes.publishedBy", {
                      name: version.publishedByName ?? t("wiki.draft.provenance.someone"),
                      at: formatDateTime(version.publishedAt),
                    })}
                  </p>
                ) : null}
                {version.changeSetId ? (
                  <VersionAttribution
                    projectId={project.id}
                    id={version.changeSetId}
                    title={version.changeSetTitle}
                  />
                ) : null}
              </div>
              {changes || restorable ? (
                <>
                  <div className="hidden shrink-0 items-center gap-1 sm:flex">
                    {changes ? (
                      <Button asChild variant="ghost" size="xs">
                        <Link
                          href={changes}
                          aria-label={t("wiki.history.changesOf", { version: version.version })}
                        >
                          {t("wiki.history.changes")}
                        </Link>
                      </Button>
                    ) : null}
                    {restorable ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        ref={(node) => {
                          triggers.current.set(`restore:${version.version}`, node);
                        }}
                        onClick={() => openRestore(version.version)}
                      >
                        <RotateCcw aria-hidden="true" />
                        {t("wiki.history.restore")}
                      </Button>
                    ) : null}
                  </div>
                  <div className="shrink-0 sm:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t("wiki.history.actions", { version: version.version })}
                          ref={(node) => {
                            triggers.current.set(`menu:${version.version}`, node);
                          }}
                        >
                          <Ellipsis aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-44">
                        {changes ? (
                          <DropdownMenuItem onSelect={() => navigate(changes)}>
                            {t("wiki.history.changes")}
                          </DropdownMenuItem>
                        ) : null}
                        {restorable ? (
                          <DropdownMenuItem onSelect={() => openRestore(version.version)}>
                            {t("wiki.history.restore")}
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </>
              ) : null}
            </li>
          );
        })}
      </ol>
      <RestoreDialog
        projectId={project.id}
        target={restore}
        onClose={() => setRestore(null)}
        returnFocus={() => {
          const menu = triggers.current.get(`menu:${restoredFrom.current}`);
          return menu?.offsetParent
            ? menu
            : (triggers.current.get(`restore:${restoredFrom.current}`) ?? null);
        }}
      />
    </section>
  );
}

/** N-1 history carries an unredacted summary; check the set before displaying that prose. */
function VersionAttribution({
  projectId,
  id,
  title,
}: {
  projectId: string;
  id: string;
  title?: string | null;
}) {
  const { t } = useTranslation();
  const set = useChangeSet(projectId, id);
  return (
    <Link href={changeSetPath(projectId, id)} className="text-sm text-accent-text hover:underline">
      {set.data && !set.data.hiddenItems
        ? title || t("wiki.changes.review")
        : t("wiki.changes.hiddenTitle")}
    </Link>
  );
}

export function WikiActivity({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const activity = useWikiActivity(projectId);
  const list = useRef<HTMLOListElement>(null);
  const focusFrom = useRef<number | null>(null);
  const length = activity.data?.pages.flatMap((page) => page.items).length ?? 0;
  useEffect(() => {
    if (focusFrom.current !== null && length > focusFrom.current) {
      (list.current?.children[focusFrom.current] as HTMLElement | undefined)?.focus();
      focusFrom.current = null;
    }
  }, [length]);
  if (activity.isPending) {
    return <Skeleton className="h-48" />;
  }
  if (activity.isError && !activity.isFetchNextPageError) {
    return <Notice tone="danger">{activity.error.message}</Notice>;
  }
  const entries = activity.data.pages.flatMap((page) => page.items);
  return (
    <section data-esi-content="wiki" className="mx-auto flex max-w-3xl flex-col gap-5">
      <h2 className="text-xl font-semibold tracking-tight text-fg">{t("wiki.changes.activity")}</h2>
      {!entries.length ? (
        <p className="text-sm text-fg-secondary">{t("wiki.changes.noActivity")}</p>
      ) : null}
      <ol ref={list} className="divide-y divide-line">
        {entries.map((entry) => (
          <li key={entry.id} tabIndex={-1} className="flex flex-col gap-2 py-5 outline-offset-2">
            <Link
              href={changeSetPath(projectId, entry.id)}
              className="break-words text-sm font-semibold text-fg hover:underline"
            >
              {(!entry.hiddenItems && entry.title) || t("wiki.changes.hiddenTitle")}
            </Link>
            <p className="text-xs text-fg-secondary">
              {t(`wiki.changes.origin.${entry.origin}`)} ·{" "}
              {entry.provenance.createdByName ?? t("wiki.draft.provenance.someone")} ·{" "}
              {formatDateTime(entry.publishedAt ?? entry.updatedAt)} ·{" "}
              {t(`wiki.changes.state.${entry.status}`)}
            </p>
            {entry.approvers?.length ? (
              <p className="text-xs text-fg-secondary">
                {t("wiki.changes.approvedBy", {
                  name: entry.approvers.map((a) => a.name ?? shortId(a.id)).join(", "),
                })}
              </p>
            ) : null}
            {entry.publishedBy ? (
              <p className="text-xs text-fg-secondary">
                {t("wiki.changes.publishedBy", {
                  name: entry.publishedByName ?? t("wiki.draft.provenance.someone"),
                  at: formatDateTime(entry.publishedAt ?? entry.updatedAt),
                })}
              </p>
            ) : null}
            <ul className="space-y-1 text-sm">
              {entry.items.map((item) => (
                <li key={item.id} className="flex gap-2">
                  <span className="text-fg-muted">{t(`wiki.changes.op.${item.op}`)}</span>
                  {item.publishedDocumentId && item.op !== "delete" ? (
                    <Link
                      href={wikiPath(
                        projectId,
                        item.publishedDocumentId,
                        item.publishedVersion ?? undefined,
                      )}
                      className="text-accent-text hover:underline"
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <span className="text-fg-secondary">{item.title}</span>
                  )}
                </li>
              ))}
            </ul>
            {entry.hiddenItems ? (
              <p className="text-xs text-fg-muted">{t("wiki.changes.hiddenActivity")}</p>
            ) : null}
          </li>
        ))}
      </ol>
      {activity.hasNextPage ? (
        <Button
          variant="outline"
          className="self-start"
          disabled={activity.isFetchingNextPage}
          onClick={async () => {
            focusFrom.current = entries.length;
            await activity.fetchNextPage();
          }}
        >
          {t("wiki.changes.older")}
        </Button>
      ) : null}
      {activity.isFetchNextPageError ? (
        <Notice tone="danger">{activity.error?.message}</Notice>
      ) : null}
    </section>
  );
}
